import React, { useState, useEffect } from 'react';
import { getGlobalSettings, updateGlobalSettings, subscribeToSettings } from '../services/settingsService';

export default function SEOManagement() {
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [seoTags, setSeoTags] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const unsub = subscribeToSettings((data) => {
      setSeoTitle(data.seoTitle || '');
      setSeoDescription(data.seoDescription || '');
      setSeoTags(data.seoTags || '');
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    const success = await updateGlobalSettings({ 
      seoTitle, 
      seoDescription, 
      seoTags 
    });

    if (success) {
      setMessage({ type: 'success', text: 'SEO settings updated successfully!' });
    } else {
      setMessage({ type: 'error', text: 'Failed to update SEO settings.' });
    }
    setSaving(false);
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in pl-6">
      <header className="border-b border-slate-800 pb-8">
        <h2 className="text-3xl font-bold tracking-tight text-white mb-2">SEO Management</h2>
        <p className="text-slate-400">
          Optimize your website for search engines by managing meta titles, descriptions, and keywords.
        </p>
      </header>

      {message.text && (
        <div className={`p-4 rounded-xl text-sm font-medium border ${
          message.type === 'success' 
            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
            : 'bg-red-500/10 text-red-400 border-red-500/20'
        }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSave} className="grid grid-cols-1 gap-6">
        {/* Meta Title */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
          <div>
            <h3 className="text-xl font-bold text-white mb-1">SEO Meta Title</h3>
            <p className="text-sm text-slate-400">This overrides the default site title in search engine results.</p>
          </div>
          <input
            type="text"
            className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all font-medium"
            placeholder="e.g. Best Black Screen Tool for Professionals"
            value={seoTitle}
            onChange={(e) => setSeoTitle(e.target.value)}
          />
        </div>

        {/* Meta Description */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
          <div>
            <h3 className="text-xl font-bold text-white mb-1">Meta Description</h3>
            <p className="text-sm text-slate-400">A brief summary of your site (typically 150-160 characters).</p>
          </div>
          <textarea
            className="w-full h-32 px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all font-medium"
            placeholder="Describe your website to help it rank better..."
            value={seoDescription}
            onChange={(e) => setSeoDescription(e.target.value)}
          />
        </div>

        {/* Meta Tags / Keywords */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
          <div>
            <h3 className="text-xl font-bold text-white mb-1">Meta Tags (Keywords)</h3>
            <p className="text-sm text-slate-400">Separate keywords with commas (e.g. black screen, monitor test, dead pixels).</p>
          </div>
          <input
            type="text"
            className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all font-medium"
            placeholder="keyword1, keyword2, keyword3"
            value={seoTags}
            onChange={(e) => setSeoTags(e.target.value)}
          />
        </div>

        <button
          type="submit"
          disabled={saving}
          className={`w-full md:w-auto self-start px-12 py-4 rounded-xl font-bold text-white transition-all shadow-lg hover:shadow-blue-500/20 active:scale-[0.98] ${
            saving 
              ? 'bg-slate-700 cursor-not-allowed' 
              : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500'
          }`}
        >
          {saving ? 'Saving Changes...' : 'Save SEO Settings'}
        </button>
      </form>
    </div>
  );
}
